import { useEffect, useMemo, useState } from "react";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useSnackbar } from "notistack";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import DataTable from "../DataTable.jsx";
import { getDatumZeit, getEuro } from "../../services/formatService.js";
import BookingStatus from "../BookingStatus.jsx";
import BookingUserLog from "../BookingUserLog.jsx";
import {
  applyFilterWithAccessorKeys,
  applySortingWithAccessorKeys,
} from "./utils/tableUtils.js";
import {
  AddCircleOutlineOutlined,
  Delete,
  FileDownloadOutlined,
  Edit,
  MoreVert,
  OpenInNew,
} from "@mui/icons-material";

import { useClaimBookingActions } from "../../hooks/useClaimBookingActions.jsx";
import PropTypes from "prop-types";
import { useRecoilState } from "recoil";
import { backendDataAtom } from "../../recoil/atoms/backendDataAtom.js";

export default function ClaimBookingsTable({
  schaden_id,
  onRowClick = () => {},
  onClickNew = () => {},
  onClickNewAssignment = () => {},
  assignedCostCount = 0,
  compact = false,
  bookings = null,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: bookingsState,
    setState: setBookingsState,
    deleteClaimBooking,
    updateClaimBookingStatus,
    completeAssignmentBooking,
  } = useClaimBookingActions();

  const tableKey = "buchungen";
  const storageKey = "v2_table_buchungen";

  // If bookings are provided externally (compact mode), use them.
  // Otherwise use the internal state from useMultiTableData.
  const rows = bookings || bookingsState?.data || [];

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Buchungen",
        route: `/data/schadens/${schaden_id}/bookings`,
        responseKey: tableKey,
        pageSize: null,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "created_at", order: "desc" }],
          include: ["createdBy", "releasedBy", "exportedBy", "finishedBy"],
        },
        onStateChange: setBookingsState,
      },
    ],
    [schaden_id, storageKey]
  );

  const { tableStates, fetchData, updateTableState, exportData } =
    useMultiTableData(tablesConfig, enqueueSnackbar);

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    // Only fetch data if not provided externally
    if (!bookings) {
      fetchData(tableKey);
    }
  }, [schaden_id, bookings]);

  const handleChangeStatus = async (status, booking) => {
    try {
      await updateClaimBookingStatus(booking.id, status.key);
    } catch (error) {
      enqueueSnackbar("Fehler beim Ändern des Status", { variant: "error" });
    }
  };

  const handleCompleteAssignment = async (booking) => {
    try {
      await completeAssignmentBooking(booking.id);
    } catch (error) {
      enqueueSnackbar("Fehler beim Abschließen der Statistischen Buchung", {
        variant: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    await deleteClaimBooking(id);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const columns = [
    {
      accessorKey: "recipient",
      header: "Empfänger/Sender",
      size: 200,
      Cell: ({ cell, row }) => {
        const booking = rows.find((r) => r.id === row.original.id);

        const is_refund = booking?.category === "refund";
        const is_assignment = booking?.category === "assignment";

        return (
          <>
            <Typography variant="body2">{cell.getValue()}</Typography>
            {is_refund && (
              <Tooltip title={"Rückzahlung VU"}>
                <Chip label="Rückzahlung VU" size="small" color="warning" />
              </Tooltip>
            )}
            {is_assignment && (
              <Tooltip
                title={
                  "Statistische Buchung – Zahlung direkt vom Versicherer an Dienstleister"
                }
              >
                <Chip
                  label="Abtretung/Statistisch"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      accessorKey: "iban_bic",
      accessorKeys: ["iban", "bic"],
      header: "Konto",
      size: 200,
      Cell: ({ cell, row }) => {
        const iban = row.original.iban;
        const bic = row.original.bic;
        return (
          <Typography variant="body2">
            {iban} <br />
            <small>{bic}</small>
          </Typography>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Buchungszeit",
      size: 120,
      // filterVariant: "date",
      // enableColumnFilter: false,
      Cell: ({ cell }) => getDatumZeit(cell.getValue()),
    },
    {
      accessorKey: "description",
      header: "Verwendungszweck",
      size: 200,
      Footer: () => {
        return <Typography variant="body2">Summe</Typography>;
      },
    },
    {
      accessorKey: "amount",
      header: "Betrag",
      muiTableHeadCellProps: {
        align: "right",
      },
      enableColumnFilter: false,
      size: 120,
      Cell: ({ cell, row }) => {
        const amount = parseFloat(cell.getValue());
        const type = row.original.type;
        const is_refund = row.original.category === "refund";
        return (
          <Typography
            color={
              is_refund ? "warning.main" : type === "IN" ? "green" : "error"
            }
            align="right"
            fontWeight="bold"
            variant="body2"
          >
            {type === "IN" ? "" : "-"}
            {getEuro(amount)}
          </Typography>
        );
      },
      Footer: () => {
        // sum of all row amounts, considering transaction type
        const total = rows.reduce((sum, row) => {
          const amount = parseFloat(row.amount) || 0;
          return row.type === "IN" ? sum + amount : sum - amount;
        }, 0);

        return (
          <Typography align="right" fontWeight="bold" variant="body2">
            {getEuro(total)}
          </Typography>
        );
      },
    },

    {
      accessorKey: "status",
      header: "Status",
      // filterVariante: "select",
      enableColumnFilter: false,
      size: 120,
      Cell: ({ row }) => {
        const booking = rows.find((r) => r.id === row.original.id);

        if (!booking || booking.type !== "OUT") {
          return null;
        }

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              alignItems: "flex-start",
            }}
          >
            <BookingStatus
              booking={booking}
              onChange={handleChangeStatus}
              onComplete={handleCompleteAssignment}
              isLoading={tableState.isLoading}
            />
          </Box>
        );
      },
    },
    {
      accessorKeys: ["createdBy.vorname", "createdBy.nachname"],
      accessorFn: (row) =>
        row.createdBy
          ? `${row.createdBy?.vorname || ""} ${row.createdBy?.nachname || ""}`.trim()
          : "System",
      header: "Erstellt",
      size: 50,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const booking = row.original;

        return <BookingUserLog booking={booking} />;
      },
    },
    {
      accessorKey: "actions",
      header: "",
      size: 20,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const booking = row.original;
        const schaden_id = booking.schaden_id;

        return booking.type === "OUT" ? (
          <>
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, booking.id)}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && menuRowId === booking.id}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onRowClick(booking);
                }}
                disabled={booking.status !== "pending"}
              >
                <Edit
                  style={{
                    marginRight: "8px",
                  }}
                />
                {booking.type === "IN" ? "Zahlungseingang" : "Zahlungsausgang"}{" "}
                ändern
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleDelete(booking.id);
                }}
                disabled={booking.status !== "pending"}
              >
                <Delete
                  style={{
                    marginRight: "8px",
                  }}
                />
                {booking.type === "IN" ? "Zahlungseingang" : "Zahlungsausgang"}{" "}
                löschen
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  window.open(`/schaden/${booking.schaden_id}`, "_blank");
                }}
                disabled={!!schaden_id}
              >
                <OpenInNew
                  style={{
                    marginRight: "8px",
                  }}
                />{" "}
                Schaden anzeigen
              </MenuItem>
            </Menu>
          </>
        ) : null;
      },
    },
  ];

  const handleExportExcel = () => {
    exportData(tableKey, "excel");
  };

  const assignmentButtonLabel =
    assignedCostCount > 0
      ? `Statistische Buchung (${assignedCostCount})`
      : "Statistische Buchung";

  const toolbarActions = [
    {
      type: "button",
      label: "Zahlung erstellen",
      icon: <AddCircleOutlineOutlined />,
      onClick: onClickNew,
      variant: "primary",
    },
    {
      type: "button",
      label: assignmentButtonLabel,
      icon: <AddCircleOutlineOutlined />,
      onClick: onClickNewAssignment,
      variant: "secondary",
    },
    {
      label: "Excel Export",
      type: "icon",
      variant: "tertiary",
      icon: <FileDownloadOutlined />,
      onClick: handleExportExcel,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={!bookings && tableState.isLoading}
      hasLoaded={!!bookings || tableState.hasLoaded}
      enableFooter={true}
      enableHeader={!compact}
      enableSorting={!compact}
      manualSorting={false}
      enablePagination={false}
      enableBottomToolbar={false}
      enableGlobalFilter={!compact}
      enableColumnFilters={!compact}
      enableColumnHiding={!compact}
      topToolbarIcons={!compact}
      onChangeSorting={(sorting) =>
        !compact &&
        updateTableState(tableKey, {
          sorting: applySortingWithAccessorKeys(sorting, columns),
        })
      }
      onChangeFilters={(filters) =>
        !compact &&
        updateTableState(tableKey, {
          filters: applyFilterWithAccessorKeys(filters, columns),
        })
      }
      onChangeGlobalFilter={(searchword) =>
        !compact && updateTableState(tableKey, { searchword })
      }
      toolbarActions={compact ? [] : toolbarActions}
    />
  );
}

ClaimBookingsTable.propTypes = {
  schaden_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onRowClick: PropTypes.func,
  onClickNew: PropTypes.func,
  onClickNewAssignment: PropTypes.func,
  assignedCostCount: PropTypes.number,
  compact: PropTypes.bool,
  bookings: PropTypes.array,
};
